import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { api, type ApiApplication, type ApiAppUser } from "../../api.ts";
import { CopyOnceCallout } from "../../components/copy-once-callout.tsx";
import { ErrorBanner } from "../../components/error-banner.tsx";
import { getTestRedirectUri } from "../../lib/test-redirect.ts";
import { AppNameHeader } from "./app-name-header.tsx";
import { DangerSection } from "./danger-section.tsx";
import { OidcConfigTable } from "./oidc-config-section.tsx";
import { RedirectUrisForm } from "./redirect-uris-section.tsx";
import { Step } from "./step.tsx";
import { TestAction } from "./test-section.tsx";
import { UsersForm } from "./users-section.tsx";

export function Application() {
  const { appId } = useParams<{ appId: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const [application, setApplication] = useState<ApiApplication | null>(null);
  const [appUsers, setAppUsers] = useState<ApiAppUser[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [revealedClientSecret, setRevealedClientSecret] = useState<string | null>(
    null,
  );

  // The just-created secret arrives via router state when the user is
  // redirected from the dashboard's "create app" flow. Consume it once and
  // wipe the history entry's state so a reload doesn't keep showing it.
  useEffect(() => {
    const stateClientSecret = (location.state as { clientSecret?: string } | null)
      ?.clientSecret;
    if (stateClientSecret) {
      setRevealedClientSecret(stateClientSecret);
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!appId) {
      return;
    }
    Promise.all([api.getApplication(appId), api.listAppUsers(appId)])
      .then(([appResponse, usersResponse]) => {
        setApplication(appResponse.application);
        setAppUsers(usersResponse.users);
      })
      .catch((error: Error) => setErrorMessage(error.message));
  }, [appId]);

  if (!appId) {
    return null;
  }
  if (errorMessage && !application) {
    return (
      <p className="font-mono text-xs uppercase tracking-wider text-accent">
        {errorMessage}
      </p>
    );
  }
  if (!application) {
    return <p className="text-sm text-muted">Loading…</p>;
  }

  function reportError(error: unknown) {
    setErrorMessage(error instanceof Error ? error.message : String(error));
  }

  async function handleRename(newName: string) {
    try {
      const response = await api.renameApplication(appId!, newName);
      setApplication(response.application);
    } catch (error) {
      reportError(error);
    }
  }

  async function handleRotateSecret() {
    try {
      const response = await api.rotateClientSecret(appId!);
      setApplication(response.application);
      setRevealedClientSecret(response.clientSecret);
    } catch (error) {
      reportError(error);
    }
  }

  async function handleSaveRedirectUris(uris: string[]) {
    const response = await api.updateRedirectUris(appId!, uris);
    setApplication(response.application);
  }

  async function handleCreateAppUser(email: string, accessCode: string) {
    const response = await api.createAppUser(appId!, email, accessCode);
    setAppUsers((current) => [...current, response.user]);
  }

  async function handleDeleteAppUser(userId: string) {
    try {
      await api.deleteAppUser(appId!, userId);
      setAppUsers((current) => current.filter((user) => user.id !== userId));
    } catch (error) {
      reportError(error);
    }
  }

  async function handleDeleteApplication() {
    try {
      await api.deleteApplication(appId!);
      navigate("/");
    } catch (error) {
      reportError(error);
    }
  }

  async function handleAddTestRedirectUri() {
    const testUri = getTestRedirectUri();
    if (application!.redirectUris.includes(testUri)) {
      return;
    }
    try {
      await handleSaveRedirectUris([...application!.redirectUris, testUri]);
    } catch (error) {
      reportError(error);
    }
  }

  return (
    <>
      <p className="mb-6 font-mono text-xs uppercase tracking-wider">
        <Link to="/" className="no-underline">
          ← Dashboard
        </Link>
      </p>

      <AppNameHeader name={application.name} onRename={handleRename} />

      <ErrorBanner message={errorMessage} className="mt-4" />

      {revealedClientSecret ? (
        <div className="mt-6">
          <CopyOnceCallout
            message="Copy the client secret now — it will not be shown again."
            value={revealedClientSecret}
          />
        </div>
      ) : null}

      <div className="mt-6">
        <Step
          title="Redirect URIs"
          description="The URL your app sends users to after they sign in. Must match exactly what your OIDC client library uses."
        >
          <RedirectUrisForm
            uris={application.redirectUris}
            onSave={handleSaveRedirectUris}
          />
        </Step>

        <Step
          title="Access codes"
          description="Map a long secret to a user email. Anyone who enters the code signs in as that user."
        >
          <UsersForm
            users={appUsers}
            onCreate={handleCreateAppUser}
            onDelete={handleDeleteAppUser}
          />
        </Step>

        <Step
          title="Test sign-in"
          description="Run the access-code prompt against this application yourself."
        >
          <TestAction
            application={application}
            ready={appUsers.length > 0}
            onAddTestUri={handleAddTestRedirectUri}
          />
        </Step>

        <Step
          title="OIDC configuration"
          description="Reference values to paste into your OIDC client library."
        >
          <OidcConfigTable
            application={application}
            onRotateSecret={handleRotateSecret}
          />
        </Step>
      </div>

      <div className="mt-12">
        <DangerSection
          appName={application.name}
          onDelete={handleDeleteApplication}
        />
      </div>
    </>
  );
}
